import { Injectable } from '@angular/core';
import { FieldType, StepperModel, WidthClass } from '@thehubz/client/core';
import { BuyoutType, LicenseRegion } from '@thehubz/shared/model';
import { DownloadPurposeData } from '../../../models/download-purpose-data';
import { buyoutRightsField, buyoutRightsFieldMakeOS, buyoutRightsFieldSeat } from './asset-settings-stepper.constants';

@Injectable({
  providedIn: 'root',
})
export class AssetBuyoutRightsStepperConfigService {
  /**
   * This function is used to create the stepper config for the asset upload component.
   */
  getAssetBuyoutRightsStepperConfig = (availableTitles: string[], editMode = false): StepperModel => {
    return {
      stepID: 'assetBuyoutRights',
      stepLabel: editMode ? $localize`Edit buyout rights` : $localize`Add buyout rights`,
      leftArea: ['usageRightsGeneralInformation'],
      stepper: [
        {
          stepperId: '1',
          stepperName: $localize`General information`,
          stepperGroupName: 'usageRightsGeneralInformation',
          selected: false,
          visited: false,
          disable: true,
          role: 'usagerights',
          formFields: [{ ...buyoutRightsField(availableTitles) }],
          actions: [
            {
              actionLabel: $localize`:action:Next`,
              actionType: 'buyoutRights',
              goToStep: 2,
              actionTypePosition: 'right',
              icon: 'chevron_right',
              alignIcon: 'right',
              disable: false,
              visible: true,
            },
          ],
        },
        {
          stepperId: '2',
          stepperName: $localize`Buyout rights`,
          stepperGroupName: 'buyoutRights',
          selected: false,
          visited: false,
          disable: true,
          role: 'usagerights',
          formFields: [
            {
              formTypeId: '1',
              formFieldName: 'buyoutRightsFormArray',
              fieldType: FieldType.FormArray,
              fieldLabel: '',
              hideLabel: true,
              placeholder: '',
              inputType: '',
              mandatory: false,
              widthClass: WidthClass.WFull,
              formArrayExtraClasses: 'flex flex-wrap grow items-center',
              disable: false,
              visible: true,
              formFieldGroup: [
                {
                  formTypeId: '1',
                  formFieldName: 'licenseRegion',
                  fieldType: FieldType.Dropdown,
                  fieldLabel: $localize`License region`,
                  placeholder: $localize`:placeholder:License region`,
                  widthClass: WidthClass.W15,
                  options: LicenseRegion,
                  value: undefined,
                  dropDownSearch: true,
                  visible: true,
                },
                {
                  formTypeId: '2',
                  formFieldName: 'channel',
                  fieldType: FieldType.Dropdown,
                  fieldLabel: $localize`Channel`,
                  placeholder: $localize`:placeholder:Channel`,
                  widthClass: WidthClass.W15,
                  options: DownloadPurposeData.lists,
                  value: undefined,
                  dropDownSearch: true,
                  visible: true,
                },
                {
                  formTypeId: '3',
                  formFieldName: 'buyoutType',
                  fieldType: FieldType.Dropdown,
                  fieldLabel: $localize`Buyout type`,
                  placeholder: $localize`:placeholder:Buyout type`,
                  widthClass: WidthClass.W15,
                  options: BuyoutType,
                  value: undefined,
                  dropDownSearch: true,
                  visible: true,
                },
                {
                  formTypeId: '4',
                  formFieldName: 'expiryDate',
                  fieldType: FieldType.Date,
                  fieldLabel: $localize`Expiry date`,
                  placeholder: $localize`DD/MM/YYYY`,
                  minDate: new Date(),
                  widthClass: WidthClass.W15,
                  validators: {
                    required: true,
                  },
                  value: '',
                  visible: true,
                },
                {
                  formTypeId: '3',
                  formFieldName: 'noExpiry',
                  fieldType: FieldType.Checkbox,
                  fieldLabel: $localize`No Expiry date`,
                  placeholder: '',
                  widthClass: WidthClass.W15,
                  value: '',
                  validators: {
                    required: false,
                  },
                  visible: true,
                },
              ],
              formArrayActionType: 'add-n-remove',
            },
          ],
          actions: [
            {
              actionLabel: $localize`:action:Next`,
              actionType: 'next',
              goToStep: 3,
              actionTypePosition: 'right',
              icon: 'chevron_right',
              alignIcon: 'right',
              disable: false,
              visible: true,
            },
            {
              actionLabel: $localize`:action:Back`,
              actionType: 'back',
              goToStep: 1,
              actionTypePosition: 'left',
              icon: 'chevron_left',
              alignIcon: 'left',
              disable: false,
              visible: true,
            },
          ],
        },
        {
          stepperId: '3',
          stepperName: $localize`Review and submit`,
          stepperGroupName: 'reviewAndSubmit',
          selected: false,
          visited: false,
          disable: true,
          formFields: [],
          actions: [
            {
              actionLabel: $localize`:action:Back`,
              actionType: 'back',
              goToStep: 2,
              actionTypePosition: 'left',
              icon: 'chevron_left',
              alignIcon: 'left',
              disable: false,
              visible: true,
            },
            {
              actionLabel: $localize`:action:Save`,
              actionType: 'saveBuyoutRights',
              goToStep: 5,
              actionTypePosition: 'right',
              icon: 'save',
              alignIcon: 'left',
              disable: false,
              visible: true,
            },
          ],
        },
      ],
    };
  };

  getAssetBuyoutRightsMakeOSStepperConfig = (availableTitles: string[], editMode = false, newStepperId?: string, isRequired = true): StepperModel => {
    const newStepper: StepperModel = {
      stepID: 'assetBuyoutRights',
      stepLabel: editMode ? $localize`Edit buyout rights` : $localize`Add buyout rights`,
      stepper: [
        {
          stepperId: newStepperId ? newStepperId : '1',
          stepperName: newStepperId ? $localize`Buyout rights` : $localize`General information`,
          stepperGroupName: 'usageRightsGeneralInformation',
          selected: false,
          visited: false,
          disable: true,
          role: 'usagerights',
          formFields: [{ ...buyoutRightsFieldMakeOS(availableTitles, false, '', isRequired) }],
          actions: [
            {
              actionLabel: $localize`:action:Next`,
              actionType: 'buyoutRights',
              goToStep: 2,
              actionTypePosition: 'right',
              icon: 'chevron_right',
              alignIcon: 'right',
              disable: false,
              visible: true,
            },
          ],
        },
      ],
    };

    const reviewStep = {
      stepperId: '2',
      stepperName: $localize`Review and submit`,
      stepperGroupName: 'reviewAndSubmit',
      selected: false,
      visited: false,
      disable: true,
      formFields: [],
      actions: [
        {
          actionLabel: $localize`:action:Back`,
          actionType: 'back',
          goToStep: 1,
          actionTypePosition: 'left',
          icon: 'chevron_left',
          alignIcon: 'left',
          disable: false,
          visible: true,
        },
        {
          actionLabel: $localize`:action:Save`,
          actionType: 'saveBuyoutRights',
          actionTypePosition: 'right',
          icon: 'save',
          alignIcon: 'left',
          disable: false,
          visible: true,
        },
      ],
    };

    if (!newStepperId) {
      newStepper.stepper.push(reviewStep);
    } else {
      const backAction = {
        actionLabel: $localize`:action:Back`,
        actionType: 'back',
        goToStep: newStepperId ? parseInt(newStepperId) - 1 : 0,
        actionTypePosition: 'left',
        icon: 'chevron_left',
        alignIcon: 'left',
        disable: false,
        visible: true,
      };
      newStepper.stepper[0].actions?.push(backAction);
    }

    return newStepper;
  };

  getAssetSeatBuyoutRightsStepperConfig = (availableTitles: string[], editMode = false, newStepperId?: string): StepperModel => {
    const newStepper: StepperModel = {
      stepID: 'assetBuyoutRights',
      stepLabel: editMode ? $localize`Edit Buyout rights` : $localize`Add buyout rights`,
      leftArea: ['usageRightsGeneralInformation'],
      stepper: [
        {
          stepperId: newStepperId ? newStepperId : '1',
          stepperName: newStepperId ? $localize`Buyout rights` : $localize`General information`,
          stepperGroupName: 'usageRightsGeneralInformation',
          selected: false,
          visited: false,
          disable: true,
          role: 'usagerights',
          formFields: [{ ...buyoutRightsFieldSeat(availableTitles, undefined, newStepperId) }],
          actions: [
            {
              actionLabel: $localize`:action:Next`,
              actionType: 'buyoutRights',
              goToStep: newStepperId ? parseInt(newStepperId) + 1 : 2,
              actionTypePosition: 'right',
              icon: 'chevron_right',
              alignIcon: 'right',
              disable: false,
              visible: true,
            },
          ],
        },
      ],
    };

    const reviewStep = {
      stepperId: '2',
      stepperName: $localize`Review and submit`,
      stepperGroupName: 'reviewAndSubmit',
      selected: false,
      visited: false,
      disable: true,
      formFields: [],
      actions: [
        {
          actionLabel: $localize`:action:Back`,
          actionType: 'back',
          goToStep: 1,
          actionTypePosition: 'left',
          icon: 'chevron_left',
          alignIcon: 'left',
          disable: false,
          visible: true,
        },
        {
          actionLabel: $localize`:action:Save`,
          actionType: 'saveBuyoutRights',
          goToStep: 5,
          actionTypePosition: 'right',
          icon: 'save',
          alignIcon: 'left',
          disable: false,
          visible: true,
        },
      ],
    };

    if (!newStepperId) {
      newStepper.stepper.push(reviewStep);
    } else {
      const backAction = {
        actionLabel: $localize`:action:Back`,
        actionType: 'back',
        goToStep: newStepperId ? parseInt(newStepperId) - 1 : 0,
        actionTypePosition: 'left',
        icon: 'chevron_left',
        alignIcon: 'left',
        disable: false,
        visible: true,
      };
      newStepper.stepper[0].actions?.push(backAction);
    }
    return newStepper;
  };
}
